import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CircleHelp,
  Loader2,
  Search,
  Stethoscope,
  Trash2,
  XCircle,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BuyAiCreditsDialog } from "@/components/ai-credits/BuyAiCreditsDialog";
import { CreditsExhaustedNotice } from "@/components/ai-credits/CreditsExhaustedNotice";
import { useAccount } from "@/contexts/account.context";
import { useAuth } from "@/contexts/auth.context";
import { toast } from "@/hooks/use-toast";
import { ehAssinante } from "@/lib/account-access-context";
import { tenantAtivo } from "@/lib/hey-api-client";
import { AiCreditsExhaustedError } from "@/services/ai-credit-packs.service";
import { cn } from "@/lib/utils";
import {
  PAGE_TYPE_LABEL,
  diagnosticService,
  type DiagnosticAnalysisSummary,
  type DiagnosticJob,
  type DiagnosticPageType,
  type DiagnosticResultFull,
} from "@/services/diagnostic.service";

const POLL_INTERVAL_MS = 2000;
// Pior caso no worker: fetch (25s) + Shopify (15s) + LLM (180s) + fila. 6 min dá
// folga para a fila sem deixar a UI presa para sempre num job morto.
const POLL_TIMEOUT_MS = 6 * 60 * 1000;

const PAGE_TYPES: DiagnosticPageType[] = ["home", "category", "product"];

const FORM_LABEL: Record<DiagnosticPageType, { label: string; cta: string }> = {
  home: { label: "Auditar Homepage", cta: "Analisar Home" },
  category: { label: "Auditar Página de Categoria", cta: "Analisar Categoria" },
  product: { label: "Auditar Página de Produto", cta: "Analisar Produto" },
};

// Variants semânticas do design system — não usar cores literais, que quebram
// no dark mode (o tema é todo baseado em tokens HSL).
const SEVERITY_VARIANT: Record<string, "destructive" | "warning" | "outline"> = {
  alto: "destructive",
  moderado: "warning",
  baixo: "outline",
};

const POTENTIAL_VARIANT: Record<string, "success" | "warning" | "outline"> = {
  alto: "success",
  medio: "warning",
  baixo: "outline",
};

const CHECKLIST_META: Record<
  string,
  { icon: typeof CheckCircle2; className: string; label: string }
> = {
  ok: { icon: CheckCircle2, className: "text-success-soft", label: "OK" },
  falha: { icon: XCircle, className: "text-error-soft", label: "Falha" },
  atencao: { icon: AlertCircle, className: "text-warning-600", label: "Atenção" },
  nao_confirmado: {
    icon: CircleHelp,
    className: "text-muted-foreground",
    label: "Não confirmado",
  },
};

function scoreTone(score: number): string {
  if (score >= 80) return "text-success-soft";
  if (score >= 50) return "text-warning-600";
  return "text-error-soft";
}

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("pt-BR");
}

/** Agrupa o checklist por dimensão preservando a ordem em que o modelo devolveu. */
function groupChecklist(
  result: DiagnosticResultFull,
): Array<[string, DiagnosticResultFull["checklist"]]> {
  const groups = new Map<string, DiagnosticResultFull["checklist"]>();
  for (const item of result.checklist) {
    const bucket = groups.get(item.dimensao);
    if (bucket) bucket.push(item);
    else groups.set(item.dimensao, [item]);
  }
  return [...groups.entries()];
}

/** Ordem fixa das dimensões — a mesma dos prompts, para a leitura não embaralhar. */
function dimensionEntries(
  result: DiagnosticResultFull,
): Array<[string, { score: number; detalhe: string }]> {
  const { dimensoes } = result;
  return [
    ["Homepage", dimensoes.homepage],
    ["Copy", dimensoes.copy],
    ["Navegação", dimensoes.navegacao],
    ["Mobile", dimensoes.mobile],
    ["Produto", dimensoes.produto],
    ["Checkout", dimensoes.checkout],
    ["Prova Social", dimensoes.prova_social],
    ["Performance", dimensoes.performance],
    ["Marketing", dimensoes.marketing],
    ["Merchandising", dimensoes.merchandising],
  ];
}

/** Abas de resultado de uma página auditada. */
function ResultTabs({ result }: { result: DiagnosticResultFull }) {
  return (
    <Tabs defaultValue="problemas" className="w-full">
      <TabsList className="w-full justify-start">
        <TabsTrigger value="problemas">
          PROBLEMAS ({result.problemas.length})
        </TabsTrigger>
        <TabsTrigger value="oportunidades">
          OPORTUNIDADES ({result.oportunidades.length})
        </TabsTrigger>
        <TabsTrigger value="checklist">
          CHECKLIST ({result.checklist.length})
        </TabsTrigger>
        <TabsTrigger value="plano">
          PLANO DE AÇÃO ({result.plano_acao.length})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="problemas" className="space-y-4 mt-4">
        {result.problemas.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum problema relevante encontrado nesta página
          </div>
        ) : (
          result.problemas.map((problema, index) => (
            <Card key={`${problema.categoria}-${problema.titulo}-${index}`} className="card-kpi-secondary">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  {/* h3 cru em vez de CardTitle: o CardTitle força UPPERCASE,
                      que destruiria a leitura do texto gerado pela IA. */}
                  <h3 className="text-sm font-medium text-foreground">
                    {problema.titulo}
                  </h3>
                  <div className="flex shrink-0 gap-1.5">
                    <Badge variant={SEVERITY_VARIANT[problema.severidade] ?? "outline"}>
                      {problema.severidade}
                    </Badge>
                    <Badge variant="secondary">{problema.categoria}</Badge>
                  </div>
                </div>
                <CardDescription>{problema.descricao}</CardDescription>
              </CardHeader>
            </Card>
          ))
        )}
      </TabsContent>

      <TabsContent value="oportunidades" className="space-y-4 mt-4">
        {result.oportunidades.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhuma oportunidade identificada nesta página
          </div>
        ) : (
          result.oportunidades.map((oportunidade, index) => (
            <Card key={`${oportunidade.titulo}-${index}`} className="card-kpi-secondary">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-medium text-foreground">
                    {oportunidade.titulo}
                  </h3>
                  <Badge
                    variant={POTENTIAL_VARIANT[oportunidade.potencial] ?? "outline"}
                    className="shrink-0"
                  >
                    potencial {oportunidade.potencial}
                  </Badge>
                </div>
                <CardDescription>{oportunidade.descricao}</CardDescription>
              </CardHeader>
            </Card>
          ))
        )}
      </TabsContent>

      <TabsContent value="checklist" className="space-y-4 mt-4">
        {groupChecklist(result).map(([dimensao, itens]) => (
          <Card key={dimensao} className="card-kpi-secondary">
            <CardHeader>
              <CardTitle>{dimensao}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {itens.map((item, index) => {
                  const meta =
                    CHECKLIST_META[item.status] ?? CHECKLIST_META.nao_confirmado;
                  const Icon = meta.icon;
                  return (
                    <li
                      key={`${item.item}-${index}`}
                      className="flex items-start gap-2 text-sm text-foreground"
                    >
                      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", meta.className)} />
                      <span className="flex-1">{item.item}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {meta.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        ))}
      </TabsContent>

      <TabsContent value="plano" className="space-y-4 mt-4">
        {result.plano_acao.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhuma ação prioritária para esta página
          </div>
        ) : (
          [...result.plano_acao]
            .sort((a, b) => a.prioridade - b.prioridade)
            .map((acao) => (
              <Card key={`${acao.prioridade}-${acao.titulo}`} className="card-kpi-secondary">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-medium text-primary-foreground">
                      {acao.prioridade}
                    </span>
                    <div className="flex-1 space-y-1">
                      <h3 className="text-sm font-medium text-foreground">
                        {acao.titulo}
                      </h3>
                      <CardDescription>{acao.descricao}</CardDescription>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <Badge variant="outline">prazo {acao.prazo}</Badge>
                        <Badge variant="outline">esforço {acao.esforco}</Badge>
                        <Badge variant="outline">impacto {acao.impacto}</Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
        )}
      </TabsContent>
    </Tabs>
  );
}

/** Estado de uma aba: em andamento (com a trilha de fases), erro, ou resultado. */
function JobPanel({ job }: { job: DiagnosticJob }) {
  if (job.status === "FAILED") {
    return (
      <Card className="card-kpi-secondary">
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <AlertCircle className="h-8 w-8 text-error-soft" />
          <h3 className="text-sm font-medium text-foreground">
            Não foi possível analisar esta página
          </h3>
          <p className="max-w-md text-sm text-muted-foreground">
            {job.error ?? "Tente novamente em alguns minutos."}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (job.status !== "COMPLETED" || !job.result) {
    const lastPhase = job.phases[job.phases.length - 1];
    return (
      <div className="space-y-6">
        <Card className="card-kpi-secondary">
          <CardHeader>
            <CardTitle>{lastPhase?.label ?? "Na fila"}</CardTitle>
            <CardDescription>
              Estamos lendo a página e avaliando com IA. Isso costuma levar cerca de um
              minuto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {job.phases.length > 0 && (
              <ol className="space-y-2">
                {job.phases.map((phase, index) => (
                  <li
                    key={`${phase.key}-${index}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    {index === job.phases.length - 1 ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5 text-success-soft" />
                    )}
                    {phase.label}
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
        {/* Skeleton no formato do resultado final, para o layout não saltar
            quando a análise chegar. */}
        <Card className="card-kpi-secondary">
          <CardHeader>
            <CardTitle>Dimensões</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="card-kpi-secondary">
        <CardHeader>
          <CardTitle>Dimensões</CardTitle>
          <CardDescription>Pontuação de 0 a 100 por área avaliada</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {dimensionEntries(job.result).map(([label, dimension]) => (
              <div
                key={label}
                className="rounded-lg border border-border/60 bg-card p-3"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs font-medium text-muted-foreground">
                    {label}
                  </span>
                  <span
                    className={cn("text-2xl font-light", scoreTone(dimension.score))}
                  >
                    {dimension.score}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {dimension.detalhe}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <ResultTabs result={job.result} />
    </div>
  );
}

export default function DiagnosticoPage() {
  const [urls, setUrls] = useState<Record<DiagnosticPageType, string>>({
    home: "",
    category: "",
    product: "",
  });
  const [analyses, setAnalyses] = useState<DiagnosticAnalysisSummary[]>([]);
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<DiagnosticJob[]>([]);
  const [activeTab, setActiveTab] = useState<DiagnosticPageType>("home");
  const [running, setRunning] = useState<DiagnosticPageType | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  // Apagar é destrutivo e irreversível (a auditoria consumiu créditos), então
  // passa por confirmação — mesmo padrão do Arquiteto.
  const [deleteTarget, setDeleteTarget] = useState<DiagnosticAnalysisSummary | null>(
    null,
  );
  // <M-280> O `402` da auditoria: a mensagem do backend, e a compra para o dono da conta.
  const [creditsExhausted, setCreditsExhausted] = useState<string | null>(null);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  // Polls vivos: permite cancelar o loop ao trocar de análise sem deixar timers
  // órfãos escrevendo estado de um job que a tela não mostra mais.
  const activePollsRef = useRef<Set<string>>(new Set());
  // Geração da view. Toda troca do que está em tela (abrir outra análise,
  // apagar, trocar de conta) incrementa este contador; as continuações
  // assíncronas capturam o valor no início e desistem se ele mudou. Sem isso,
  // uma requisição em voo escreve na tela depois de o usuário já ter navegado.
  const viewEpochRef = useRef(0);
  // Conta/subconta selecionada: o serviço lê o customer_uuid do localStorage no
  // momento da chamada, então a tela precisa se recarregar quando ela muda.
  const { selectedAccount } = useAccount();
  const accountId = selectedAccount?.id;
  // <M-280> Só o dono da conta compra créditos — é ele quem paga —, e para a loja ativa.
  const { user } = useAuth();
  const lojaAtiva = selectedAccount?.customer_uuid ?? tenantAtivo() ?? null;
  const podeComprarCreditos = ehAssinante(user) && lojaAtiva !== null;

  useEffect(() => {
    const polls = activePollsRef.current;
    return () => {
      polls.clear();
      viewEpochRef.current += 1;
    };
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      setAnalyses(await diagnosticService.listAnalyses());
    } catch {
      // Histórico é secundário: falhar aqui não pode travar a tela de análise.
    }
  }, []);

  // Recarrega e limpa a tela ao trocar de conta — sem isso o histórico e o
  // resultado continuariam exibindo dados da conta anterior.
  useEffect(() => {
    activePollsRef.current.clear();
    viewEpochRef.current += 1;
    setRunning(null);
    setCurrentAnalysisId(null);
    setJobs([]);
    void refreshHistory();
  }, [accountId, refreshHistory]);

  const upsertJob = useCallback((job: DiagnosticJob) => {
    setJobs((prev) => {
      const others = prev.filter((item) => item.pageType !== job.pageType);
      return [...others, job];
    });
  }, []);

  const pollJob = useCallback(
    (jobId: string) => {
      activePollsRef.current.add(jobId);
      const startedAt = Date.now();

      const stop = (message?: { title: string; description: string }) => {
        activePollsRef.current.delete(jobId);
        setRunning(null);
        if (message) toast({ ...message, variant: "destructive" });
      };

      const tick = async () => {
        if (!activePollsRef.current.has(jobId)) return;
        let job: DiagnosticJob;
        try {
          job = await diagnosticService.getJobStatus(jobId);
        } catch {
          if (!activePollsRef.current.has(jobId)) return;
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            stop({
              title: "Tempo excedido",
              description:
                "Não foi possível acompanhar a análise. Recarregue a página para ver o resultado.",
            });
            return;
          }
          window.setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        if (!activePollsRef.current.has(jobId)) return;

        upsertJob(job);

        if (job.status === "COMPLETED" || job.status === "FAILED") {
          activePollsRef.current.delete(jobId);
          setRunning(null);
          void refreshHistory();
          if (job.status === "FAILED") {
            toast({
              title: "Análise não concluída",
              description: job.error ?? "Não foi possível analisar a página.",
              variant: "destructive",
            });
          }
          return;
        }

        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          stop({
            title: "Tempo excedido",
            description: "A análise demorou mais que o esperado. Tente novamente.",
          });
          return;
        }
        window.setTimeout(tick, POLL_INTERVAL_MS);
      };

      window.setTimeout(tick, POLL_INTERVAL_MS);
    },
    [refreshHistory, upsertJob],
  );

  const handleAnalyze = useCallback(
    async (pageType: DiagnosticPageType) => {
      const url = urls[pageType].trim();
      if (!url) {
        toast({
          title: "Informe a URL",
          description: "Cole o endereço da página que você quer auditar.",
          variant: "destructive",
        });
        return;
      }
      const epoch = viewEpochRef.current;
      setRunning(pageType);
      try {
        const job = await diagnosticService.analyze(pageType, url, currentAnalysisId ?? undefined);
        // A tela pode ter mudado durante o POST (o usuário abriu outra análise
        // ou apagou esta): sem esta guarda, o job da análise antiga apareceria
        // misturado ao conteúdo da nova.
        if (epoch !== viewEpochRef.current) return;
        // O backend pode ter aberto uma análise nova (site diferente): manter as
        // abas antigas misturaria páginas de dois sites no mesmo score.
        if (job.analysisId !== currentAnalysisId) {
          setJobs([job]);
        } else {
          upsertJob(job);
        }
        setCurrentAnalysisId(job.analysisId);
        setActiveTab(pageType);
        pollJob(job.jobId);
      } catch (err) {
        if (epoch !== viewEpochRef.current) return;
        setRunning(null);
        if (err instanceof AiCreditsExhaustedError) {
          setCreditsExhausted(err.message);
          return;
        }
        toast({
          title: "Erro",
          description: err instanceof Error ? err.message : "Não foi possível iniciar a análise.",
          variant: "destructive",
        });
      }
    },
    [currentAnalysisId, pollJob, upsertJob, urls],
  );

  const openAnalysis = useCallback(
    async (analysisId: string) => {
      // Encerra polls da análise anterior antes de trocar o conteúdo da tela.
      activePollsRef.current.clear();
      const epoch = ++viewEpochRef.current;
      setRunning(null);
      setLoadingAnalysis(true);
      try {
        const detail = await diagnosticService.getAnalysis(analysisId);
        // Duas aberturas em sequência: a resposta lenta da primeira não pode
        // sobrescrever a seleção mais recente.
        if (epoch !== viewEpochRef.current) return;
        setCurrentAnalysisId(detail.analysisId);
        setJobs(detail.pages);
        setActiveTab(detail.pages[0]?.pageType ?? "home");
        // Retoma o acompanhamento de páginas ainda em processamento — sem isso
        // a aba ficaria presa no skeleton mesmo depois de o worker concluir.
        const pending = detail.pages.filter(
          (page) => page.status === "QUEUED" || page.status === "PROCESSING",
        );
        if (pending.length > 0) {
          setRunning(pending[0].pageType);
          pending.forEach((page) => pollJob(page.jobId));
        }
      } catch {
        if (epoch !== viewEpochRef.current) return;
        toast({
          title: "Erro",
          description: "Não foi possível carregar a análise.",
          variant: "destructive",
        });
      } finally {
        if (epoch === viewEpochRef.current) setLoadingAnalysis(false);
      }
    },
    [pollJob],
  );

  const handleDelete = useCallback(
    async (analysisId: string) => {
      try {
        await diagnosticService.deleteAnalysis(analysisId);
        if (analysisId === currentAnalysisId) {
          activePollsRef.current.clear();
          viewEpochRef.current += 1;
          // Sem isto os três botões ficavam desabilitados para sempre quando a
          // análise apagada era a que estava rodando.
          setRunning(null);
          setCurrentAnalysisId(null);
          setJobs([]);
        }
        void refreshHistory();
      } catch {
        toast({
          title: "Erro",
          description: "Não foi possível apagar a análise.",
          variant: "destructive",
        });
      } finally {
        setDeleteTarget(null);
      }
    },
    [currentAnalysisId, refreshHistory],
  );

  const visibleJobs = PAGE_TYPES.map((pageType) =>
    jobs.find((job) => job.pageType === pageType),
  ).filter((job): job is DiagnosticJob => job !== undefined);

  const activeJob = visibleJobs.find((job) => job.pageType === activeTab) ?? visibleJobs[0];
  const completedScores = visibleJobs
    .map((job) => job.score)
    .filter((score): score is number => typeof score === "number");
  const overallScore = completedScores.length
    ? Math.round(completedScores.reduce((sum, score) => sum + score, 0) / completedScores.length)
    : null;
  const currentSite = analyses.find((item) => item.analysisId === currentAnalysisId)?.siteUrl;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Stethoscope className="h-8 w-8" />
          Diagnóstico
        </h1>
        <p className="text-muted-foreground">
          Auditoria de CRO das páginas da sua loja com análise de IA
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
        {/* Coluna esquerda: formulários + histórico */}
        <div className="space-y-6">
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>Análise com IA</CardTitle>
              <CardDescription>
                Informe as URLs das páginas que deseja auditar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {PAGE_TYPES.map((pageType, index) => (
                <div
                  key={pageType}
                  className={cn("space-y-2", index > 0 && "border-t border-border/60 pt-4")}
                >
                  <Label htmlFor={`diagnostic-url-${pageType}`}>
                    {FORM_LABEL[pageType].label}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id={`diagnostic-url-${pageType}`}
                      placeholder="https://exemplo.com"
                      value={urls[pageType]}
                      onChange={(event) =>
                        setUrls((prev) => ({ ...prev, [pageType]: event.target.value }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") void handleAnalyze(pageType);
                      }}
                      disabled={running !== null}
                    />
                    <Button
                      size="sm"
                      onClick={() => void handleAnalyze(pageType)}
                      disabled={running !== null}
                      className="shrink-0"
                      aria-label={FORM_LABEL[pageType].cta}
                      aria-busy={running === pageType}
                    >
                      {running === pageType ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        FORM_LABEL[pageType].cta
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle actions={<Badge variant="secondary">{analyses.length}</Badge>}>
                Histórico de Análises
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analyses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma análise ainda
                </div>
              ) : (
                <ul className="space-y-2">
                  {analyses.map((analysis) => (
                    <li key={analysis.analysisId}>
                      <div
                        className={cn(
                          "group flex items-start gap-2 rounded-lg border p-3 transition-colors",
                          analysis.analysisId === currentAnalysisId
                            ? "border-primary bg-primary/5"
                            : "border-border/60 hover:bg-muted/50",
                        )}
                      >
                        <button
                          type="button"
                          className="flex-1 text-left"
                          onClick={() => void openAnalysis(analysis.analysisId)}
                        >
                          <p className="truncate text-sm font-medium text-foreground">
                            {analysis.siteUrl}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(analysis.updatedAt)}
                            {analysis.scoreGeral !== null && (
                              <>
                                {" · "}
                                <span
                                  className={cn(
                                    "font-medium",
                                    scoreTone(analysis.scoreGeral),
                                  )}
                                >
                                  {analysis.scoreGeral}/100
                                </span>
                              </>
                            )}
                          </p>
                        </button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
                          onClick={() => setDeleteTarget(analysis)}
                          aria-label={`Apagar análise de ${analysis.siteUrl}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Coluna direita: resultado */}
        <div className="space-y-6">
          {loadingAnalysis ? (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </>
          ) : visibleJobs.length === 0 ? (
            <Card className="card-kpi-secondary">
              <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
                <Search className="h-10 w-10 text-muted-foreground" />
                <h3 className="text-sm font-medium text-foreground">
                  Nenhuma análise selecionada
                </h3>
                <p className="max-w-sm text-sm text-muted-foreground">
                  Informe a URL da homepage, de uma categoria ou de um produto e a IA vai
                  auditar a página com o olhar de um especialista em CRO.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="card-kpi-primary">
                <CardHeader>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-medium text-foreground">
                        {currentSite ?? activeJob?.url}
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        {visibleJobs.length} página
                        {visibleJobs.length > 1 ? "s" : ""} auditada
                        {visibleJobs.length > 1 ? "s" : ""}
                        {activeJob?.finishedAt &&
                          ` · ${formatDateTime(activeJob.finishedAt)}`}
                      </p>
                    </div>
                    {overallScore !== null && (
                      <div className="flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2">
                        <span
                          className={cn(
                            "text-3xl font-light",
                            scoreTone(overallScore),
                          )}
                        >
                          {overallScore}
                        </span>
                        <span className="text-2xs font-medium uppercase leading-tight text-muted-foreground">
                          Score
                          <br />
                          Geral
                        </span>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>

              <Tabs
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as DiagnosticPageType)}
                className="w-full"
              >
                <TabsList className="w-full justify-start">
                  {visibleJobs.map((job) => (
                    <TabsTrigger key={job.pageType} value={job.pageType}>
                      {PAGE_TYPE_LABEL[job.pageType]}
                      {typeof job.score === "number" && ` (${job.score}/100)`}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {visibleJobs.map((job) => (
                  <TabsContent
                    key={job.pageType}
                    value={job.pageType}
                    className="space-y-6 mt-4"
                  >
                    <JobPanel job={job} />
                  </TabsContent>
                ))}
              </Tabs>
            </>
          )}
        </div>
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar esta análise?</AlertDialogTitle>
            <AlertDialogDescription>
              A auditoria de {deleteTarget?.siteUrl} será removida do histórico. Rodar de
              novo consome créditos de IA.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) void handleDelete(deleteTarget.analysisId);
              }}
            >
              Apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={creditsExhausted !== null}
        onOpenChange={(open) => {
          if (!open) setCreditsExhausted(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Créditos de IA insuficientes</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <CreditsExhaustedNotice
                  detail={creditsExhausted}
                  onBuyCredits={
                    podeComprarCreditos
                      ? () => {
                          setCreditsExhausted(null);
                          setBuyCreditsOpen(true);
                        }
                      : undefined
                  }
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setCreditsExhausted(null)}>
              Fechar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {podeComprarCreditos && lojaAtiva && (
        <BuyAiCreditsDialog
          open={buyCreditsOpen}
          onOpenChange={setBuyCreditsOpen}
          customerUuid={lojaAtiva}
          storeName={selectedAccount?.name ?? null}
        />
      )}
    </div>
  );
}
